import {
  deleteTrip as deleteTripFromAPI,
  fetchFleetTrips,
} from "@/api/vtnz-backend/trips";
import type { Trip } from "@/domains/trips";
import type { RouteName } from "@/router";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import type { Vehicle } from "../vehicles";
import { TripCore } from "./core/tripCoreClass";
import type { TripLocationCore } from "./core/tripLocationCoreClass";
import { TripICE } from "./ICE/tripICEClass";
import type { TripPlanningReturn } from "./types";

export const useTripStore = defineStore("tripStore", () => {
  // ---------------------------------- hooks --------------------------------- //

  const router = useRouter();

  // ---------------------------------- State --------------------------------- //

  const fetchState = ref<FetchState>("idle");
  const fetchErrorMsg = ref<string | null>(null);

  const trips = ref<Trip[] | null>(null);
  const selectedTripId = ref<string | null>(null);

  const planningOrigin = ref<TripLocationCore | null>(null);
  const planningDestination = ref<TripLocationCore | null>(null);
  const planningWaypoints = ref<TripLocationCore[]>([]);
  const planningVehicle = ref<Vehicle | null>(null);

  // --------------------------------- Getters -------------------------------- //

  /** helper flag to indicated async fetching is in operation. Good for loading animations. */
  const isFetching = computed(() => fetchState.value === "loading");

  /** The currently selected trip, if one is selected. */
  const getSelectedTrip = computed(() =>
    trips.value?.find((t) => t.id === selectedTripId.value),
  );

  /** All trips currently known to the application. */
  const getTrips = computed<Trip[]>(() => trips.value ?? []);

  /** Trips that have been persisted to the backend. */
  const getSavedTrips = computed<Trip[]>(
    () => trips.value?.filter((t) => t.externalId) ?? [],
  );

  /** Trips that exist only in the current session. */
  const getUnsavedTrips = computed<Trip[]>(
    () => trips.value?.filter((t) => !t.externalId) ?? [],
  );

  /**
   * Looks up a trip by its external id - useful for translating a
   * backend-sourced id (e.g. a notification's `related_route_plan_id`) into
   * the trip, since dashboardNavStore's `showTripDetails` keys off the
   * trip's local `.id`, not its `externalId`.
   */
  function getTripByExternalId(tripExternalId: string): Trip | undefined {
    return trips.value?.find((t) => t.externalId === tripExternalId);
  }

  // --------------------------------- Actions -------------------------------- //

  /**
   * Adds a trip to the store or replaces the existing instance with the same id.
   *
   * Triggers reactive updates after mutating the underlying array.
   */
  function addTrip(trip: Trip) {
    const existingIndex = trips.value?.findIndex(
      (existing) => existing.id === trip.id,
    );

    if (
      existingIndex !== undefined &&
      existingIndex !== -1 &&
      trips.value?.[existingIndex]
    ) {
      trips.value[existingIndex] = trip;
      return;
    }

    if (trips.value) trips.value.push(trip);
    else trips.value = [trip];
  }

  /**
   * Removes a trip from the store.
   *
   * @param tripId Local id of the trip to remove.
   */
  function removeTrip(tripId: string) {
    trips.value = trips.value
      ? trips.value.filter((t) => t.id !== tripId)
      : null;
  }

  /**
   * Deletes a trip from the database.
   *
   * NOTE: this does not remove the trip from the store, it only deletes it from the database.
   *
   * @param tripId The ID of the trip to delete.
   */
  async function deleteTrip(
    tripId: string,
  ): Promise<{ success: boolean; error?: string }> {
    const tripToDelete = trips.value?.find((t) => t.id === tripId);
    if (!tripToDelete) {
      return { success: false, error: "Trip not found" };
    }

    if (!tripToDelete.externalId) {
      // Trip only exists locally - nothing to delete on the backend.
      return { success: true };
    }

    const result = await deleteTripFromAPI(tripToDelete.externalId);

    if (!result.success) {
      return { success: false, error: result.errorMsg };
    }

    return { success: true };
  }

  /**
   * Displays the specified trip.
   *
   * Sets the trip as the active selection and navigates to its itinerary page.
   * Showing a trip automatically replaces any previously displayed trip.
   *
   * @param tripId The ID of the trip to display.
   */
  async function showTrip(tripId: string): Promise<void> {
    const trip = trips.value?.find((t) => t.id === tripId);
    if (!trip) return;
    // showing a trip is the boundary at which any in-progress planning draft
    // for a *different* trip should be discarded, so it can never leak in.
    clearPlanningData();
    selectedTripId.value = tripId;

    if (trip.status === "unplanned") await trip.planTrip();

    const routeName: RouteName = "trip-itinerary";
    router.push({ name: routeName });
  }

  function clearSelectedTrip() {
    selectedTripId.value = null;
    clearPlanningData();
  }

  /**
   * Navigates away from the current trip and clears it as the active
   * selection once that navigation has resolved, so views reacting to
   * `getSelectedTrip` don't briefly render their "no trip" state while
   * still on the trip's route.
   */
  async function exitToHome() {
    await router.push({ name: "home" });
    clearSelectedTrip();
  }

  async function fetchTrips(vehicles: Vehicle[]) {
    fetchState.value = "loading";
    fetchErrorMsg.value = null;

    const fetchRes = await fetchFleetTrips();

    if (!fetchRes.success) {
      fetchErrorMsg.value = fetchRes.errorMsg;
      fetchState.value = "error";
      return;
    }

    const resolvedTrips: TripCore[] = [];
    const errors: string[] = [];

    for (const dto of fetchRes.data) {
      try {
        const vehicle = vehicles.find(
          (v) => v.externalId && v.externalId === dto.vehicle_id,
        );

        if (!vehicle) {
          throw new Error(
            `Vehicle ${dto.vehicle_id} could not be resolved for trip ${dto.id}`,
          );
        }

        resolvedTrips.push(TripCore.fromDTO(dto, vehicle));
      } catch (error) {
        errors.push(
          error instanceof Error ? error.message : "Unknown trip error",
        );
      }
    }

    trips.value = resolvedTrips;

    if (errors.length) {
      fetchErrorMsg.value = `${errors.length} trip(s) could not be loaded`;
      fetchState.value = "partial";
    } else {
      fetchState.value = "success";
    }
  }

  function clearPlanningData() {
    planningDestination.value = null;
    planningOrigin.value = null;
    planningVehicle.value = null;
    planningWaypoints.value = [];
  }

  function setPlanningOrigin(location: TripLocationCore | null) {
    planningOrigin.value = location;
  }

  function setPlanningDestination(location: TripLocationCore | null) {
    planningDestination.value = location;
  }

  function setPlanningWaypoints(locations: TripLocationCore[]) {
    planningWaypoints.value = locations;
  }

  function addPlanningWaypoint(location: TripLocationCore) {
    planningWaypoints.value = [...planningWaypoints.value, location];
  }

  function updatePlanningWaypoint(location: TripLocationCore) {
    planningWaypoints.value = planningWaypoints.value.map((existing) =>
      existing.id === location.id ? location : existing,
    );
  }

  function removePlanningWaypoint(id: string) {
    planningWaypoints.value = planningWaypoints.value.filter(
      (location) => location.id !== id,
    );
  }

  function setPlanningVehicle(vehicle: Vehicle | null) {
    planningVehicle.value = vehicle;
  }

  /**
   * Copies the selected trip's origin/destination/waypoints/vehicle into the
   * `planning*` fields, for re-entering the planning flow to edit a trip
   * that's already been created/planned. Locations are cloned so editing the
   * draft can't mutate the live trip (or vice versa) until it's committed.
   *
   * Safe to call unconditionally (e.g. on every planning view's mount): it
   * only seeds when planning state is completely empty, so it never
   * clobbers a live edit already in progress.
   */
  function seedPlanningFromSelectedTrip() {
    const trip = getSelectedTrip.value;
    if (!trip) return;

    const isPlanningEmpty =
      !planningOrigin.value &&
      !planningDestination.value &&
      !planningWaypoints.value.length &&
      !planningVehicle.value;
    if (!isPlanningEmpty) return;

    planningOrigin.value = trip.origin?.clone() ?? null;
    planningDestination.value = trip.destination?.clone() ?? null;
    planningWaypoints.value = trip.locations
      .slice(1, -1)
      .map((location) => location.clone());
    planningVehicle.value = trip.vehicle;
  }

  /**
   * Materializes the current planning state into a real trip: mutates and
   * re-plans the selected trip if one exists (recalculating), otherwise
   * constructs and plans a new one. Either way, planning state is cleared
   * once the trip has been committed.
   *
   * Only constructs `TripICE` — vehicle-type-driven trip class switching
   * (e.g. rebuilding as a future `TripEV`) isn't supported yet; `TripCore`'s
   * own DTO/query dispatch already throws for non-ICE vehicles today, so
   * this is unreachable in the current ICE-only PoC.
   */
  async function commitPlanning({
    locations,
    vehicle,
    startingLoad,
  }: {
    locations: TripLocationCore[];
    vehicle: Vehicle | null;
    startingLoad: number;
  }): Promise<TripPlanningReturn> {
    const existingTrip = getSelectedTrip.value;

    if (existingTrip) {
      existingTrip.locations = locations;
      existingTrip.startingLoad = startingLoad;
      await existingTrip.changeVehicle(vehicle);
      const result = await existingTrip.planTrip({ forceReCalc: true });
      clearPlanningData();
      if (result.success) {
        const routeName: RouteName = "trip-itinerary";
        router.push({ name: routeName });
      }
      return result;
    }

    const newTrip = new TripICE({
      locations,
      startingLoad,
      vehicle: vehicle ?? undefined,
      vehicleExternalId: vehicle?.externalId ?? null,
    });
    addTrip(newTrip);
    await showTrip(newTrip.id); // also clears planning data, since it's shown

    return newTrip.status === "failed"
      ? { success: false, error: newTrip.planningFailureReason ?? "Failed to plan trip" }
      : { success: true, status: "successfully planned" };
  }

  function $reset() {
    // state
    fetchState.value = "idle";
    trips.value = null;
    selectedTripId.value = null;
    fetchErrorMsg.value = null;
    // planning data
    clearPlanningData();
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** indicates the stores current data fetching state */
    fetchState,
    /** error message for last trips fetching error if there is one. */
    fetchErrorMsg,
    /** the trip being planned's destination. */
    planningDestination,
    /** the trip being planned's origin. */
    planningOrigin,
    /** the trip being planned's vehicle. */
    planningVehicle,
    /** the trip being planned's waypoints. */
    planningWaypoints,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicated async fetching is in operation. Good for loading animations. */
    isFetching,
    /** The currently selected trip, if one is selected. */
    getSelectedTrip,
    /** All trips currently known to the application. */
    getTrips,
    /** Trips that have been persisted to the backend. */
    getSavedTrips,
    /** Trips that exist only in the current session. */
    getUnsavedTrips,
    /** Looks up a trip by its external id. */
    getTripByExternalId,

    // -------------------------------- Actions ------------------------------- //

    /**
     * Adds a trip to the store or replaces the existing instance with the same id.
     *
     * Triggers reactive updates after mutating the underlying array.
     */
    addTrip,

    /**
     * Removes a trip from the store.
     *
     * @param tripId Local id of the trip to remove.
     */
    removeTrip,

    /**
     * Displays the specified trip.
     *
     * Sets the trip as the active selection and navigates to its itinerary page.
     * Showing a trip automatically replaces any previously displayed trip.
     *
     * @param tripId The ID of the trip to display.
     */
    showTrip,

    /**
     * Clears the currently displayed trip,
     */
    clearSelectedTrip,

    /**
     * Navigates to home and clears the currently displayed trip once that
     * navigation resolves.
     */
    exitToHome,

    /**
     * Clears trip planning data state.
     */
    clearPlanningData,

    /** Sets the trip being planned's origin. */
    setPlanningOrigin,

    /** Sets the trip being planned's destination. */
    setPlanningDestination,

    /** Replaces the trip being planned's waypoints. */
    setPlanningWaypoints,

    /** Appends a waypoint to the trip being planned. */
    addPlanningWaypoint,

    /** Replaces an existing planning waypoint (matched by id) in place. */
    updatePlanningWaypoint,

    /** Removes a waypoint from the trip being planned by id. */
    removePlanningWaypoint,

    /** Sets the trip being planned's vehicle. */
    setPlanningVehicle,

    /**
     * Seeds planning state from the selected trip's current origin,
     * destination, waypoints and vehicle, for editing an already-planned
     * trip via the planning flow. No-ops if no trip is selected.
     */
    seedPlanningFromSelectedTrip,

    /**
     * Materializes the current planning state into a real trip -
     * recalculating the selected trip if one exists, otherwise creating and
     * planning a new one. Clears planning state once committed.
     */
    commitPlanning,

    /**
     * Deletes a trip from the database.
     *
     * NOTE: this does not remove the trip from the store, it only deletes it from the database.
     *
     * @param tripId The ID of the trip to delete.
     */
    deleteTrip,

    /** Fetches the fleets trips from the backend and assigns it to sate. */
    fetchTrips,

    /** Resets the store to its initial state. */
    $reset,
  };
});
